// src/hooks/useDiscountManager.js
import { useReducer, useMemo, useCallback, useState } from "react";
import useFetchDiscounts from "./useFetchDiscounts";
import { api } from "../api";
import { useCart } from "../context/CartContext";

const initialState = {
  nextDiscount: null,
  amountTillNextDiscount: null,
  discountProgress: 0,
  hasNextDiscount: false,
  hasAnyDiscounts: false,
};

function discountReducer(state, action) {
  switch (action.type) {
    case "UPDATE_DISCOUNT":
      return { ...state, ...action.payload };
    default:
      return state;
  }
}

export function useDiscountManager() {
  const { cart } = useCart();

  // Calculate totals here
  const regularItems = cart.filter((item) => !item.baseBundleId);
  const bundleItems = cart.filter((item) => item.baseBundleId);
  const regularTotal = regularItems.reduce(
    (sum, item) => sum + item.price * item.qty,
    0
  );
  const bundleTotal = bundleItems.reduce(
    (sum, item) => sum + item.price * item.qty,
    0
  );
  const total = regularTotal + bundleTotal;

  const {
    discounts = [],
    loading,
    error: fetchError,
    refetch,
  } = useFetchDiscounts();
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [error, setError] = useState("");
  const [warning, setWarning] = useState("");
  const [couponCodeUsed, setCouponCodeUsed] = useState("");

  const [state, dispatch] = useReducer(discountReducer, initialState);

  const updateDiscount = useCallback((newState) => {
    dispatch({ type: "UPDATE_DISCOUNT", payload: newState });
  }, []);

  // 1) Pre-parse qualifications
  const parsed = useMemo(
    () =>
      discounts.map((d) => ({
        ...d,
        qualifications:
          typeof d.qualifications === "string"
            ? JSON.parse(d.qualifications)
            : d.qualifications,
      })),
    [discounts]
  );

  // 2) Sort by value once
  const sorted = useMemo(
    () => [...parsed].sort((a, b) => parseFloat(b.value) - parseFloat(a.value)),
    [parsed]
  );

  // 3) Derive best/next/progress
  const { best, next, nextThreshold, progress } = useMemo(() => {
    let best = null,
      next = null,
      nextThreshold = Infinity;
    for (const d of sorted) {
      const min = d.qualifications.min_order || 0;
      if (regularTotal >= min) {
        if (!best || parseFloat(d.value) > parseFloat(best.value)) best = d;
      } else if (min < nextThreshold) {
        next = d;
        nextThreshold = min;
      }
    }
    const currMin = best?.qualifications.min_order || 0;
    const pct =
      nextThreshold < Infinity
        ? ((regularTotal - currMin) / (nextThreshold - currMin)) * 100
        : 100;
    return {
      best,
      next,
      nextThreshold,
      progress: Math.min(100, Math.max(0, pct)),
    };
  }, [sorted, regularTotal]);

  // Update state based on derived values
  useMemo(() => {
    updateDiscount({
      nextDiscount: next,
      amountTillNextDiscount: Math.max(0, nextThreshold - regularTotal),
      discountProgress: progress,
      hasNextDiscount: !!next,
      hasAnyDiscounts: discounts.length > 0,
    });
  }, [next, nextThreshold, total, progress, discounts, updateDiscount]);

  // 4) Coupon application stays the same logic
  const applyCoupon = useCallback(
    async (code) => {
      setError("");
      setWarning("");
      let couponData = null;

      if (code) {
        try {
          const { status, data } = await api.isValidCoupon({ code });
          if (status === 200 && data.valid) {
            couponData = data;
          } else {
            throw new Error(data.message || "Invalid coupon");
          }
        } catch (err) {
          setError(err.message);
          setAppliedCoupon(null); // Clear the applied coupon
          setCouponCodeUsed("");
          return { success: false, newDiscount: null, warning: false };
        }
      } else {
        // If code is an empty string or null/undefined, treat it as “no coupon applied”
        setAppliedCoupon(null);
        setCouponCodeUsed("");
        return { success: false, newDiscount: null, warning: false };
      }

      const newVal = couponData?.value || 0;
      setCouponCodeUsed(code);

      if (parseFloat(newVal) <= parseFloat(best?.value || 0)) {
        setWarning("A higher discount is already applied.");
        setAppliedCoupon(null); // Clear the applied coupon
        return { success: false, newDiscount: best, warning: true };
      }

      setAppliedCoupon({ value: newVal, description: couponData.description });
    //   console.log(couponData)
      return { success: true, newDiscount: couponData, warning: false };
    },
    [best]
  );

  const memoizedState = useMemo(
    () => ({
      ...state,
      // raw
      discounts,
      loading,
      error: error || fetchError,
      refetch,
      couponCodeUsed,
      // derived
      appliedDiscount: best,
      appliedCoupon,

      // actions
      applyCoupon,
      warning,

      // totals
      regularTotal,
      bundleTotal,
      total,
    }),
    [
      state,
      discounts,
      loading,
      fetchError,
      refetch,
      best,
      appliedCoupon,
      error,
      warning,
      regularTotal,
      bundleTotal,
      total,
      updateDiscount,
      couponCodeUsed,
    ]
  );

  return memoizedState;
}
